<script setup lang="ts">
// @ts-nocheck
import findLast from 'lodash/findLast';
import orderBy from 'lodash/orderBy';
import first from 'lodash/first'
import {useCartStore} from "../../stores/cart";
import CustomNumberInput from "../CustomNumberInput.vue";
import {computed, onMounted, ref, watch} from "vue";
import type {Ref} from "vue";
import {label} from "../../mixins/translations.js";
import {type ProductResponse} from "../../../responses/ProductResponse";
import {useProductStore} from "../../stores/product";
import {type ProductVariantResponse} from "../../../responses/ProductVariantResponse";
import {Money} from "../../../utils/money";
import {type PriceResponse} from "../../../responses/PriceResponse";
import FavoriteButton from "./FavoriteButton.vue";
import {useOfferStore} from "../../stores/offer";
import Tooltip from "../Tooltip.vue";
import {TooltipProvider} from "reka-ui";
import NoAccountModal from "./NoAccountModal.vue";

type Events = {
    selectedVariantChanged: ProductVariantResponse
}

interface Props {
    product: ProductResponse
    showInput: Boolean
}

const props = defineProps<Props>();
const amount: Ref<number> = ref(1);
const cart = useCartStore();
const offer = useOfferStore();
const productStore = useProductStore();
const currentPrice: Ref<PriceResponse> = ref({});
const currentVariant: Ref<ProductVariantResponse> = ref(null);
const showPriceVat = ref(false);
const loggedIn: Ref<boolean> = ref(window.logged_in ?? false);
const noAccountModal: Ref<boolean> = ref(false);
if(sessionStorage.getItem('show_vat_incl') === "1"){
    showPriceVat.value = true;
}
const addToCart = () => {
    cart.addItem({
        amount: amount.value,
        variant_id: productStore.selectedVariant.id
    });

    //todo reactivate when implementing google
    // window.trackAddToCartGoogle(props.product, amount.value, 'add_to_cart');
    reset();
}
const addToOffer = () => {
    if(!loggedIn.value) {
        noAccountModal.value = true;
        return;
    }

    offer.addItem({
        amount: amount.value,
        variant_id: productStore.selectedVariant.id
    })
}
const reset = () => {
    handleAmountChange(1);
}
const handleAmountChange = (newAmount) => {
    amount.value = newAmount;
    updatePrice();
    productStore.events.emit('quantityChanged', newAmount);
}

onMounted(() => {
    currentVariant.value = first(orderBy(props.product.variants, 'price.price_incl'));
});

const updatePrice = () => {
    currentPrice.value = findLast(productStore.selectedVariant?.tieredPrices, function (price: PriceResponse) {
        return price.quantity <= amount.value;
    })
}

const tieredPrices = computed(() => {
    return productStore.selectedVariant?.tieredPrices
        ?.filter(price => price.quantity > 1)
        ?.sort((a, b) => a.quantity - b.quantity);
})

watch(
    () => currentVariant,
    () => {
        productStore.setSelectedVariant(currentVariant.value);
        updatePrice();
    },
    {deep: true}
)

const toggleVat = () => {
    showPriceVat.value = !showPriceVat.value;
    sessionStorage.setItem('show_vat_incl', showPriceVat.value ? "1" : "0");
}
</script>
<template>
    <TooltipProvider>
        <div class="flex mt-4" v-if="currentPrice">
            <div class="w-full">
                <p class="mt-4 text-2xl">
                    <strong>{{ Money.format(showPriceVat ? currentPrice.price_incl : currentPrice.price_excl, currentPrice.currency) }}</strong>
                    <span class="text-sm" v-if="showPriceVat">&nbsp;{{ label('site.order.incl_vat') }}</span>
                    <span class="text-sm" v-else>&nbsp;{{ label('site.order.excl_vat') }}</span>
                </p>
            </div>
            <div class="flex items-center justify-center w-full mt-5">
                <span class="text-sm mr-2">{{ label('site.order.excl_vat') }}</span>
                <label for="toggle" class="flex items-center cursor-pointer" @click.prevent="toggleVat">
                    <input type="checkbox" id="toggle" class="sr-only peer" :checked="showPriceVat" />
                    <div class="block relative bg-gray-200 w-12 h-6 p-1 rounded-full before:absolute before:bg-white before:w-4 before:h-4 before:p-1 before:rounded-full before:transition-all before:duration-500 before:left-1 peer-checked:before:left-7 peer-checked:before:bg-gray-500"></div>
                </label>
                <span class="text-sm ml-2">{{ label('site.order.incl_vat') }}</span>
            </div>
        </div>
        <div class="flex flex-col py-2 gap-1 " v-if="tieredPrices">
            <div class="flex flex-row gap-1" v-for="price in tieredPrices">
                <div v-text="label('site.graduate-price.from', { 'amount': price.quantity})"></div>
                <div v-if="showPriceVat"> {{ Money.format(price.price_incl, price.currency) }} {{ label('site.order.incl_vat') }}</div>
                <div v-else> {{ Money.format(price.price_excl, price.currency) }} {{ label('site.order.excl_vat')}}</div>
            </div>
        </div>
        <div class="flex flex-col md:flex-row md:items-center gap-2 mt-8" v-if="currentPrice">
            <div class="flex flex-wrap sm:flex-nowrap items-center gap-2 sm:gap-4">
                <custom-number-input v-if="showInput" size="large" :value="amount" @input="handleAmountChange"></custom-number-input>
            </div>
            <div class="flex items-center flex-wrap gap-2">
                <button @click.prevent="addToCart" type="submit" class="cursor-pointer font-medium text-sm lg:text-base px-4 lg:px-10 py-2 lg:py-4 leading-6 text-white transition bg-green hover:bg-green-500 hover:duration-500 flex justify-center hover:bg-secondary">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-shopping-cart mr-2">
                        <circle cx="8" cy="21" r="1"></circle>
                        <circle cx="19" cy="21" r="1"></circle>
                        <path d="M2.05 2.05h2l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12"></path>
                    </svg>
                    <span class="">{{ label('site.product.add_to_cart') }}</span>
                </button>

                <button type="button" @click.prevent="addToOffer" class="cursor-pointer font-medium text-sm lg:text-base px-4 lg:px-10 py-2 lg:py-4 leading-6 text-white transition bg-primary hover:bg-primary-700 hover:duration-500 flex justify-center" v-if="currentPrice">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-file-icon lucide-file mr-2">
                        <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z"></path>
                        <path d="M14 2v4a2 2 0 0 0 2 2h4"></path>
                    </svg>
                    <span class="">{{ label('site.product.add_to_offer') }}</span>
                </button>

<!--                <Tooltip :content="label('site.favorites.add')">-->
<!--                    <favorite-button :product="product" icon-class="cursor-pointer font-medium border text-base px-2 py-2 lg:px-4 lg:py-4 leading-6 text-black transition bg-white border-gray-200 hover:bg-gray-100 hover:duration-500 flex justify-center"></favorite-button>-->
<!--                </Tooltip>-->
<!--                <a href="#" class="font-medium border text-base px-2 py-2 lg:px-4 lg:py-4 leading-6 text-black transition bg-white border-gray-200 hover:bg-gray-100 hover:duration-500 flex justify-center">-->
<!--                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-pencil-ruler">-->
<!--                        <path d="M13 7 8.7 2.7a2.41 2.41 0 0 0-3.4 0L2.7 5.3a2.41 2.41 0 0 0 0 3.4L7 13"></path>-->
<!--                        <path d="m8 6 2-2"></path>-->
<!--                        <path d="m18 16 2-2"></path>-->
<!--                        <path d="m17 11 4.3 4.3c.94.94.94 2.46 0 3.4l-2.6 2.6c-.94.94-2.46.94-3.4 0L11 17"></path>-->
<!--                        <path d="M21.174 6.812a1 1 0 0 0-3.986-3.987L3.842 16.174a2 2 0 0 0-.5.83l-1.321 4.352a.5.5 0 0 0 .623.622l4.353-1.32a2 2 0 0 0 .83-.497z"></path>-->
<!--                        <path d="m15 5 4 4"></path>-->
<!--                    </svg>-->
<!--                </a>-->
            </div>
        </div>
    </TooltipProvider>

    <NoAccountModal v-if="noAccountModal" @close="noAccountModal = false"></NoAccountModal>
</template>
